import React, { Component } from "react";
import { Table, Spinner, Input, Button } from "reactstrap";
import { toast } from "react-toastify";

import { getOnboardingPresettings, updateOnboardingPresetting } from "../../services/onboardingPresettingsService";

const BORDER = "rgba(255, 255, 255, 0.12)";
const tableStyle = { maxWidth: 400, borderColor: BORDER };
const headerCellStyle = { borderColor: BORDER };

const LVM_SUFFIX = "_level_value_multiplier";
const isLvmName = (name) => typeof name === "string" && name.endsWith(LVM_SUFFIX);
const lvmGestureLabel = (name) => name.slice(0, -LVM_SUFFIX.length);

class OnboardingPresettingsTab extends Component {
  state = {
    duration: null,
    lvmGestures: [],
    loading: true,
    saving: false,
  };

  async componentDidMount() {
    await this.fetchPresettings();
  }

  handleDurationChange = (value) => {
    this.setState((prev) => ({
      duration: {
        ...(prev.duration || {}),
        value:
          value === ""
            ? null
            : String(value)
                .replace(",", ".")
                .split(".")[0]
                .replace(/[^0-9]/g, ""),
      },
    }));
  };

  handleLvmChange = (id, value) => {
    this.setState((prev) => ({
      lvmGestures: prev.lvmGestures.map((gesture) => {
        if (gesture.id !== id) return gesture;
        return { ...gesture, value: value === "" ? null : value };
      }),
    }));
  };

  onSave = async () => {
    const { duration, lvmGestures } = this.state;
    this.setState({ saving: true });
    try {
      const updates = [];
      if (duration?.id) {
        const intValue = duration.value === "" || duration.value == null ? null : parseInt(duration.value, 10);
        const numValue = intValue === null || Number.isNaN(intValue) ? null : parseFloat(`${intValue}.0`);
        updates.push(updateOnboardingPresetting(duration.id, { name: "duration", value: numValue }));
      }
      lvmGestures.forEach((gesture) => {
        if (!gesture.id) return;
        const floatValue = gesture.value === "" || gesture.value == null ? null : parseFloat(gesture.value);
        updates.push(updateOnboardingPresetting(gesture.id, { name: gesture.name, value: floatValue }));
      });
      await Promise.all(updates);
      toast.success("Saved onboarding presettings");
      await this.fetchPresettings();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      this.setState({ saving: false });
    }
  };

  async fetchPresettings() {
    try {
      const { data } = await getOnboardingPresettings();
      let duration = null;
      const lvmGestures = [];
      if (Array.isArray(data)) {
        data.forEach((item) => {
          if (!item || !item.id || !item.name) return;
          if (item.name === "duration") {
            duration = {
              id: item.id,
              value: item.value === null || item.value === undefined ? null : parseInt(item.value, 10),
            };
          } else if (isLvmName(item.name)) {
            lvmGestures.push({ id: item.id, name: item.name, value: item.value });
          }
        });
        lvmGestures.sort((a, b) => (a.id == null || b.id == null ? 0 : a.id - b.id));
      }
      this.setState({ duration, lvmGestures, loading: false });
    } catch (e) {
      toast.error("Failed to load onboarding presettings");
      this.setState({ loading: false });
    }
  }

  render() {
    const { duration, lvmGestures, loading, saving } = this.state;
    if (loading) {
      return <Spinner color="primary" />;
    }
    return (
      <>
        <p className="mb-4">
          These settings will be applied to all stores in their first days, please edit with caution.
        </p>
        <div className="mb-5">
          <h6 className="text-uppercase mb-2">Onboarding Duration (in days)</h6>
          <div style={{ maxWidth: 200 }}>
            <Input
              type="number"
              step="1"
              value={duration?.value ?? ""}
              onChange={(e) => this.handleDurationChange(e.target.value)}
              placeholder="Enter duration (integer)"
            />
          </div>
        </div>

        <div className="mb-5">
          <h6 className="text-uppercase mb-2">LVM Multiplier per Gesture</h6>
          <p className="text-muted mb-2" style={{ fontSize: "0.85rem" }}>
            Multiplies the alert priority score so the extra alerts generated during onboarding rise in the labeling
            queue.
          </p>
          <Table bordered size="sm" style={tableStyle}>
            <thead className="text-primary">
              <tr>
                <th style={headerCellStyle}>Gesture type</th>
                <th style={headerCellStyle}>LVM Multiplier</th>
              </tr>
            </thead>
            <tbody>
              {lvmGestures.map((gesture) => (
                <tr key={gesture.id}>
                  <td className="text-capitalize">{lvmGestureLabel(gesture.name)}</td>
                  <td>
                    <Input
                      type="number"
                      step="0.01"
                      inputMode="decimal"
                      value={gesture.value ?? ""}
                      onChange={(e) => this.handleLvmChange(gesture.id, e.target.value)}
                      placeholder="Enter multiplier (float)"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        <div className="mt-3">
          <Button color="success" onClick={this.onSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </>
    );
  }
}

export default OnboardingPresettingsTab;
